import React, { useCallback, useMemo } from 'react';

import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { useVerificationStore } from '@/modules/user/components/Verification/stores/useVerification.store';
import { VerificationType } from '@/modules/user/components/Verification/verification.types';

import { usePersonalInfoStore } from '../PersonalInfoForm/stores';

import EmailVerificationModal from './EmailVerificationModal';
import PhoneNumberVerificationModal from './PhoneNumberVerificationModal';

interface Props {
  handleOpenLetsGetStartedModal: () => void;
  onSetVisible: (value: boolean) => void;
  onSetSuccessModal: () => void;
}

const InvestingVerificationModal: React.FC<Props> = ({ handleOpenLetsGetStartedModal, onSetSuccessModal, onSetVisible }) => {
  const { handleAddPermanentSnackbar } = useSnackbar();
  const verificationChannels = useVerificationStore((state) => state.state.verificationChannels);
  const setState = useVerificationStore((state) => state.setState);
  const currentVerificationChannel = useMemo(() => verificationChannels?.[0], [verificationChannels]);
  
  const currentState = usePersonalInfoStore((state) => state.state);

  const removeVerificationChannel = useCallback((verificationType: VerificationType) => {
    const filteredVerificationChannels = [...verificationChannels || []]
      .filter((verificationChannel) => verificationChannel !== verificationType);
    setState({ verificationChannels: filteredVerificationChannels });
  }, [verificationChannels, setState]);

  const getSnackBarText = useCallback((verificationType: VerificationType) => {
    if (verificationType === VerificationType.EMAIL) {
      return 'Email address has been verified!';
    }

    if (verificationType === VerificationType.SMS) {
      return 'Phone number has been verified!';
    }
  }, []);

  const handleBack = useCallback(() => {
    onSetVisible(false);
    handleOpenLetsGetStartedModal();
  }, [handleOpenLetsGetStartedModal, onSetVisible]);

  const handleNext = useCallback((verificationType: VerificationType) => {
    const isLastVerificationChannel = verificationChannels?.[verificationChannels?.length - 1] === verificationType;
    removeVerificationChannel(verificationType);

    if (isLastVerificationChannel) {
      onSetSuccessModal();
    } else {
      handleAddPermanentSnackbar({
        text: getSnackBarText(verificationType),
        state: SnackbarState.positive,
        closeIcon: true,
        autoRemove: true,
      });
    }
  }, [verificationChannels, removeVerificationChannel, handleAddPermanentSnackbar, onSetSuccessModal, getSnackBarText]);

  return (
    <>
      {currentVerificationChannel === VerificationType.EMAIL && currentState?.email && (
        <EmailVerificationModal
          email={currentState?.email}
          onSetVisible={onSetVisible}
          onBack={handleBack}
          onNext={handleNext}
        />
      )}

      {currentVerificationChannel === VerificationType.SMS && currentState?.phone && (
        <PhoneNumberVerificationModal
          phoneNumber={currentState?.phone}
          onSetVisible={onSetVisible}
          onBack={handleBack}
          onNext={handleNext}
        />
      )}
    </>
  );
};

export default InvestingVerificationModal;
